import { Bookmark, BookmarkBorder } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { InView } from 'react-intersection-observer';

import { CardFlair, CreditsPill, DurationPill, EthicsPill } from '@/components';
import { ComplianceTypes } from '@/constants';
import { EVENT_CONSTANTS } from '@/services';

import { Link, LinkVariants } from '../../../Link';
import {
  EpisodeCardMediumDimensions,
  EpisodeCardVariants,
} from '../../constants';
import { episodeCardStyles as styles } from '../../styles';
import { EpisodeCardComponentProps } from '../../useEpisodeCard';
import { EpisodeToolbar } from '../EpisodeToolbar';
import { EpisodeCardMediumSkeleton } from './EpisodeCardMediumSkeleton';

interface EpisodeCardMediumProps extends EpisodeCardComponentProps {
  onLinkClick?: () => void;
  stretch?: boolean;
}

export const EpisodeCardMedium = ({
  episode,
  onLinkClick,
  referrer = {},
  navigationLink,
  impressionRef,
  onToggleBookmark,
  isBookmarked,
  coverLoaded,
  setCoverLoaded,
  hideToolbar,
  isEpisodeEthics,
  compliance,
  stretch = false,
}: EpisodeCardMediumProps) => {
  const location = {
    ...referrer,
    component: EVENT_CONSTANTS.COMPONENT.EPISODE_CARD,
    variants: {
      ...referrer.variants,
      episodeCardVariant: EpisodeCardVariants.MEDIUM,
    },
  };

  const isCPE = compliance === ComplianceTypes.CPE;

  return (
    <Box
      ref={impressionRef}
      sx={[
        styles.root,
        {
          flexDirection: 'column',
          maxHeight: stretch ? 'none' : EpisodeCardMediumDimensions.height,
          width: stretch ? '100%' : EpisodeCardMediumDimensions.width,
          maxWidth: stretch ? '100%' : EpisodeCardMediumDimensions.width,
          '&:has(#episode-card-text:hover) #episode-card-toolbar': {
            transform: 'translateY(0)',
            opacity: 1,
          },
          '&:has(#episode-card-cover:hover) #episode-card-toolbar': {
            transform: 'translateY(0)',
            opacity: 1,
          },
        },
      ]}>
      <Link
        to={navigationLink}
        onClick={onLinkClick}
        state={{ referrer: location }}>
        <InView rootMargin="1000px 0px 1000px 0px" threshold={0} triggerOnce>
          {({ inView, ref: coverRef }) => (
            <Box
              sx={[
                styles.cover,
                stretch
                  ? {
                      width: '100%',
                      aspectRatio: `${EpisodeCardMediumDimensions.coverImageWidth} / ${EpisodeCardMediumDimensions.coverImageHeight}`,
                    }
                  : {
                      width: EpisodeCardMediumDimensions.coverImageWidth,
                      height: EpisodeCardMediumDimensions.coverImageHeight,
                    },
              ]}
              ref={coverRef}
              id="episode-card-cover">
              {inView && (
                <>
                  <Box
                    component="img"
                    id="episode-card-img"
                    sx={[
                      styles.coverImg,
                      styles.coverImgWide,
                      { display: coverLoaded ? 'block' : 'none' },
                    ]}
                    src={episode.cover}
                    alt="cover"
                    onLoad={() => setCoverLoaded(true)}
                  />
                  {episode.episodeAgeTag === 'new' && <CardFlair />}
                  <Box
                    component="img"
                    sx={[
                      styles.companyLogoImg,
                      {
                        height: 40,
                        width: 40,
                      },
                      { display: coverLoaded ? 'block' : 'none' },
                    ]}
                    src={episode.companyLogo}
                    alt="company logo"
                  />
                </>
              )}
              {!coverLoaded && <EpisodeCardMediumSkeleton />}
              <Box
                id="episode-card-toolbar"
                sx={[
                  {
                    display: 'flex',
                    alignItems: 'flex-end',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    height: 46,
                    width: '100%',
                    background: `linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.50) 100%)`,
                    px: 1,
                    pb: 1,
                  },
                  hideToolbar && styles.hideToolbar,
                ]}>
                <EpisodeToolbar episode={episode} referrer={location} />
              </Box>
            </Box>
          )}
        </InView>
      </Link>
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          mt: 1,
        }}>
        <Box sx={[styles.tagContainer]}>
          {isEpisodeEthics && !isCPE && <EthicsPill />}
          {isCPE && !!episode.credits ? (
            <CreditsPill credits={episode.credits} />
          ) : (
            <DurationPill duration={episode.duration} />
          )}
          {isBookmarked ? (
            <IconButton
              onClick={onToggleBookmark}
              disableRipple
              aria-label="Remove bookmark"
              sx={styles.bookmarkIconButton}>
              <Bookmark sx={[styles.bookmarkIcon, styles.bookmarkIconActive]} />
            </IconButton>
          ) : (
            <IconButton
              onClick={onToggleBookmark}
              disableRipple
              aria-label="Add bookmark"
              sx={styles.bookmarkIconButton}>
              <BookmarkBorder sx={styles.bookmarkIcon} />
            </IconButton>
          )}
        </Box>
        <Link
          variant={LinkVariants.PLAIN}
          to={navigationLink}
          onClick={onLinkClick}
          state={{ referrer: location }}>
          <Box id="episode-card-text" sx={styles.titleContainer}>
            <Typography variant="body3SemiBold">{episode.name}</Typography>
          </Box>
        </Link>
        {isCPE && (
          <Typography variant="caption1Bold" sx={{ color: 'text.light' }}>
            {episode.fieldOfStudy}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
